import React from "react"
import {
  Box,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  RadioGroup,
  Select,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { ViraButton } from "../atoms/ViraButton"
import { ViraIconButton } from "../atoms/ViraIconButton"
import { ViraIcon } from "../atoms/ViraIcon"
import { ViraLink } from "../atoms/ViraLink"
import { ViraRadio } from "../atoms/ViraRadio"
import { ViraTextField } from "../atoms/ViraTextField"
import { ViraText } from "../atoms/ViraText"
import { SegmentedToggle } from "../molecules/SegmentedToggle"
import { TimeOfDayField } from "../molecules/TimeOfDayField"
import { type DayOfWeek } from "../molecules/DayTabs"
import { colors } from "../theme/tokens"
import { resolveViraPalette } from "../theme/resolveViraPalette"
import {
  CustomRecurrenceDialog,
  type CustomRecurrenceValue,
} from "./CustomRecurrenceDialog"

export type TechniqueScheduleMode = "one_time" | "recurring"
export type TechniqueItemType = "nudge" | "assessment"
export type NotifyScope = "only_me" | "all_practitioners"
export type TechniqueRecurrencePreset =
  | "none"
  | "daily"
  | "weekly"
  | "weekdays"
  | "custom"

export type TechniqueRecurrenceEnds =
  | { type: "never" }
  | { type: "on"; date: string }
  | { type: "after"; count: number }

export type TechniqueRecurrence = {
  preset: TechniqueRecurrencePreset
  interval: number
  unit: "week"
  ends: TechniqueRecurrenceEnds
}

export type SetTechniqueModalProps = {
  open: boolean
  mode: TechniqueScheduleMode
  onClose?: () => void
  onModeChange?: (mode: TechniqueScheduleMode) => void
  itemType?: TechniqueItemType
  onItemTypeChange?: (type: TechniqueItemType) => void
  assessmentLabel?: string
  assessmentOptions?: string[]
  onAssessmentChange?: (value: string) => void
  selectedDays?: DayOfWeek[]
  onDaysChange?: (days: DayOfWeek[]) => void
  notificationDate?: string
  onNotificationDateChange?: (value: string) => void
  notificationTime?: string
  onNotificationTimeChange?: (value: string) => void
  recurrence?: TechniqueRecurrence
  onRecurrenceChange?: (recurrence: TechniqueRecurrence) => void
  recurrenceOptions?: Array<{ value: TechniqueRecurrencePreset; label: string }>
  recurrenceSummary?: string
  notifyScope?: NotifyScope
  onNotifyScopeChange?: (scope: NotifyScope) => void
  expirationDays?: string
  expirationHours?: string
  expirationMinutes?: string
  onExpirationChange?: (part: "days" | "hours" | "minutes", value: string) => void
  /** Nudge / plan item title shown in the list. */
  itemTitle?: string
  onItemTitleChange?: (value: string) => void
  /** Client-facing goal / instruction text. */
  goal?: string
  onGoalChange?: (value: string) => void
  goalError?: string
  onSave?: () => void
  showRiskWarning?: boolean
  /** When false, hide the SMS setup sentence under the risk warning. */
  showMobileNumberPrompt?: boolean
  /** Hasura nudge schedule: domain / technique / message (web calendar). */
  nudgeDomainOptions?: string[]
  nudgeDomain?: string | null
  onNudgeDomainChange?: (domain: string | null) => void
  nudgeTechniqueOptions?: Array<{ id: number; label: string }>
  nudgeTechniqueId?: number | null
  onNudgeTechniqueChange?: (id: number | null) => void
  nudgeMessage?: string
  onNudgeMessageChange?: (message: string) => void
  /** Opens mobile-number setup from the risk-notification copy. */
  onMobileNumberSetupClick?: () => void
  /** Optional href for the mobile-number setup control (renders as a link). */
  mobileNumberSetupHref?: string
  /**
   * When false, hide recurrence end controls (Hasura weekday schedules cannot
   * store an end date/count). Defaults to true.
   */
  showRecurrenceEnds?: boolean
}

const DEFAULT_RECURRENCE: TechniqueRecurrence = {
  preset: "none",
  interval: 1,
  unit: "week",
  ends: { type: "never" },
}

/**
 * Schedule nudge / assessment with Google Calendar–style recurrence.
 */
export function SetTechniqueModal({
  open,
  mode,
  onClose,
  onModeChange,
  itemType = "assessment",
  onItemTypeChange,
  assessmentLabel = "Depression (PHQ-9)",
  assessmentOptions = ["Depression (PHQ-9)", "Anxiety (GAD-7)"],
  onAssessmentChange,
  selectedDays = [],
  onDaysChange,
  notificationDate = "",
  onNotificationDateChange,
  notificationTime = "",
  onNotificationTimeChange,
  recurrence = DEFAULT_RECURRENCE,
  onRecurrenceChange,
  recurrenceOptions,
  recurrenceSummary,
  notifyScope = "only_me",
  onNotifyScopeChange,
  expirationDays = "0",
  expirationHours = "8",
  expirationMinutes = "0",
  onExpirationChange,
  itemTitle,
  onItemTitleChange,
  goal,
  onGoalChange,
  goalError,
  onSave,
  showRiskWarning = true,
  showMobileNumberPrompt = true,
  nudgeDomainOptions,
  nudgeDomain,
  onNudgeDomainChange,
  nudgeTechniqueOptions,
  nudgeTechniqueId,
  onNudgeTechniqueChange,
  nudgeMessage,
  onNudgeMessageChange,
  onMobileNumberSetupClick,
  mobileNumberSetupHref,
  showRecurrenceEnds = true,
}: SetTechniqueModalProps) {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"))
  const isAssessment = itemType === "assessment"
  const useNudgeScheduleFields = onNudgeMessageChange != null
  const [customOpen, setCustomOpen] = React.useState(false)
  const [customDraft, setCustomDraft] = React.useState<CustomRecurrenceValue>({
    interval: 1,
    unit: "week",
    days: selectedDays,
    ends: { type: "never" },
  })

  const options =
    recurrenceOptions ??
    ([
      { value: "none", label: "Does not repeat" },
      { value: "daily", label: "Daily" },
      { value: "weekly", label: "Weekly" },
      { value: "weekdays", label: "Every weekday (Monday to Friday)" },
      { value: "custom", label: "Custom…" },
    ] as const)

  const openCustom = () => {
    setCustomDraft({
      interval: recurrence.interval || 1,
      unit: "week",
      days:
        selectedDays.length > 0
          ? selectedDays
          : (["M"] as DayOfWeek[]),
      ends: recurrence.ends ?? { type: "never" },
    })
    setCustomOpen(true)
  }

  const handlePresetChange = (preset: TechniqueRecurrencePreset) => {
    if (preset === "custom") {
      openCustom()
      return
    }
    onRecurrenceChange?.({
      ...recurrence,
      preset,
      interval: 1,
      unit: "week",
    })
    onModeChange?.(preset === "none" ? "one_time" : "recurring")
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        fullScreen={fullScreen}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.paper,
            backgroundImage: "none",
            m: fullScreen ? 0 : undefined,
            maxHeight: fullScreen ? "100%" : undefined,
            borderRadius: fullScreen ? 0 : undefined,
          }),
        }}
      >
        <DialogContent sx={{ p: { xs: 2.5, sm: 4 }, position: "relative" }}>
          <ViraIconButton
            aria-label="Close"
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              width: 40,
              height: 40,
            }}
          >
            <ViraIcon name="close" tone="muted" fontSize="small" />
          </ViraIconButton>

          <Typography
            sx={(theme) => ({
              fontFamily: '"Lato", sans-serif',
              fontWeight: 700,
              fontSize: 24,
              color:
                theme.palette.mode === "dark"
                  ? resolveViraPalette(theme).ink
                  : colors.neutral.denim,
              letterSpacing: "0.3px",
              mb: 3,
            })}
          >
            Schedule Item
          </Typography>

          <Box sx={{ mb: 3 }}>
            <SegmentedToggle
              value={itemType}
              onChange={onItemTypeChange}
              aria-label="Item type"
              options={[
                { value: "nudge", label: "Nudge" },
                { value: "assessment", label: "Assessment" },
              ]}
            />
          </Box>

          {isAssessment ? (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="assessment-label">Assessment</InputLabel>
              <Select
                labelId="assessment-label"
                label="Assessment"
                value={assessmentLabel}
                onChange={(e) => onAssessmentChange?.(String(e.target.value))}
              >
                {assessmentOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}

          {!isAssessment && useNudgeScheduleFields ? (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="nudge-domain-label">Domain Name</InputLabel>
                <Select
                  labelId="nudge-domain-label"
                  label="Domain Name"
                  value={nudgeDomain ?? ""}
                  onChange={(e) =>
                    onNudgeDomainChange?.(
                      e.target.value ? String(e.target.value) : null
                    )
                  }
                >
                  {(nudgeDomainOptions ?? []).map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="nudge-technique-label">Technique Name</InputLabel>
                <Select
                  labelId="nudge-technique-label"
                  label="Technique Name"
                  value={nudgeTechniqueId ?? ""}
                  disabled={!nudgeDomain}
                  onChange={(e) => {
                    const raw = e.target.value
                    onNudgeTechniqueChange?.(
                      raw === "" ? null : Number(raw)
                    )
                  }}
                >
                  {(nudgeTechniqueOptions ?? []).map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <ViraTextField
                fullWidth
                label="Message"
                value={nudgeMessage ?? ""}
                onChange={(e) => onNudgeMessageChange?.(e.target.value)}
                sx={{ mb: 2 }}
              />
            </>
          ) : null}

          {!isAssessment && !useNudgeScheduleFields &&
          (onItemTitleChange != null || itemTitle != null) ? (
            <ViraTextField
              fullWidth
              label="Title"
              value={itemTitle ?? ""}
              onChange={(e) => onItemTitleChange?.(e.target.value)}
              sx={{ mb: 2 }}
            />
          ) : null}

          {!isAssessment && !useNudgeScheduleFields &&
          (onGoalChange != null || goal != null) ? (
            <ViraTextField
              fullWidth
              label="Goal"
              value={goal ?? ""}
              onChange={(e) => onGoalChange?.(e.target.value)}
              error={Boolean(goalError) || !(goal ?? "").trim()}
              helperText={
                goalError ||
                (!(goal ?? "").trim() ? "Goal text is required" : undefined)
              }
              multiline
              minRows={3}
              sx={{ mb: 2 }}
            />
          ) : null}

          {isAssessment && showRiskWarning ? (
            <Box sx={{ mb: 2 }}>
              <ViraText
                tone="ink"
                sx={(theme) => ({
                  fontFamily: '"Lato", sans-serif',
                  fontSize: 16,
                  color:
                    theme.palette.mode === "dark"
                      ? resolveViraPalette(theme).ink
                      : colors.neutral.denim,
                  lineHeight: "20px",
                  mb: 1.5,
                })}
              >
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    color: colors.primary.midnight,
                    mr: 1,
                  }}
                >
                  !
                </Box>
                This assessment includes high risk questions. A risk notification
                may be triggered based on your client’s responses.
              </ViraText>
              {showMobileNumberPrompt ? (
                <ViraText
                  tone="ink"
                  sx={(theme) => ({
                    fontFamily: '"Lato", sans-serif',
                    fontSize: 16,
                    color:
                      theme.palette.mode === "dark"
                        ? resolveViraPalette(theme).ink
                        : colors.neutral.denim,
                    lineHeight: "20px",
                  })}
                >
                  When a risk alert is triggered, you will receive an email
                  notification.{" "}
                  <ViraLink
                    component={mobileNumberSetupHref ? "a" : "button"}
                    href={mobileNumberSetupHref}
                    onClick={onMobileNumberSetupClick}
                    variant="underline"
                    sx={{
                      color: colors.primary.periwinkle,
                      fontSize: 16,
                      fontWeight: 400,
                      display: "inline",
                      verticalAlign: "baseline",
                      p: 0,
                      minWidth: 0,
                      background: "none",
                      border: "none",
                    }}
                  >
                    Click here
                  </ViraLink>{" "}
                  to enter a mobile number for text notifications.
                </ViraText>
              ) : null}
            </Box>
          ) : null}

          {isAssessment && showRiskWarning ? (
            <>
              <ViraText
                sx={(theme) => ({
                  fontFamily: '"Lato", sans-serif',
                  fontSize: 16,
                  color:
                    theme.palette.mode === "dark"
                      ? resolveViraPalette(theme).muted
                      : colors.neutral.asphalt,
                  mb: 1,
                })}
              >
                If a risk alert is triggered, notify:
              </ViraText>
              <RadioGroup
                value={notifyScope}
                onChange={(_, v) => onNotifyScopeChange?.(v as NotifyScope)}
                sx={{ mb: 3 }}
              >
                <ViraRadio value="only_me" label="Only me" />
                <ViraRadio
                  value="all_practitioners"
                  label="All connected practitioners"
                />
              </RadioGroup>
            </>
          ) : isAssessment ? (
            <Box sx={{ mb: 3 }} />
          ) : (
            <ViraText
              sx={(theme) => ({
                fontFamily: '"Lato", sans-serif',
                fontSize: 16,
                color:
                  theme.palette.mode === "dark"
                    ? resolveViraPalette(theme).muted
                    : colors.neutral.asphalt,
                mb: 3,
              })}
            >
              This nudge is delivered to the client at the scheduled time.
            </ViraText>
          )}

          <ViraTextField
            fullWidth
            type="date"
            label="Start date"
            value={notificationDate}
            onChange={(e) => onNotificationDateChange?.(e.target.value)}
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
          />

          <TimeOfDayField
            label="Start time"
            value={notificationTime}
            onChange={(hhmm) => onNotificationTimeChange?.(hhmm)}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="recurrence-label">Recurrence</InputLabel>
            <Select
              labelId="recurrence-label"
              label="Recurrence"
              value={recurrence.preset}
              onChange={(e) =>
                handlePresetChange(e.target.value as TechniqueRecurrencePreset)
              }
              renderValue={() => {
                const match = options.find((o) => o.value === recurrence.preset)
                if (recurrence.preset === "custom" && recurrenceSummary) {
                  return recurrenceSummary
                }
                return match?.label ?? "Does not repeat"
              }}
            >
              {options.map((opt) => (
                <MenuItem
                  key={opt.value}
                  value={opt.value}
                  onClick={() => {
                    if (opt.value === "custom") {
                      openCustom()
                    }
                  }}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isAssessment ? (
            <Box
              sx={(theme) => ({
                border: `1px solid ${resolveViraPalette(theme).softBorder}`,
                borderRadius: "12px",
                p: 2,
                mb: 3,
                position: "relative",
              })}
            >
              <Typography
                sx={(theme) => ({
                  position: "absolute",
                  top: -10,
                  left: 16,
                  px: 1,
                  backgroundColor: theme.palette.background.paper,
                  fontFamily: '"Lato", sans-serif',
                  fontSize: 14,
                  color:
                    theme.palette.mode === "dark"
                      ? resolveViraPalette(theme).ink
                      : colors.neutral.denim,
                })}
              >
                How long the client has to respond
              </Typography>
              <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
                <ViraTextField
                  label="Days"
                  value={expirationDays}
                  onChange={(e) =>
                    onExpirationChange?.("days", e.target.value)
                  }
                  sx={{ flex: 1 }}
                />
                <ViraTextField
                  label="Hours"
                  value={expirationHours}
                  onChange={(e) =>
                    onExpirationChange?.("hours", e.target.value)
                  }
                  sx={{ flex: 1 }}
                />
                <ViraTextField
                  label="Minutes"
                  value={expirationMinutes}
                  onChange={(e) =>
                    onExpirationChange?.("minutes", e.target.value)
                  }
                  sx={{ flex: 1 }}
                />
              </Stack>
            </Box>
          ) : null}

          <ViraButton variant="primary" fullWidth onClick={onSave}>
            Save
          </ViraButton>
        </DialogContent>
      </Dialog>

      <CustomRecurrenceDialog
        open={customOpen}
        value={customDraft}
        onChange={setCustomDraft}
        onCancel={() => setCustomOpen(false)}
        showEnds={showRecurrenceEnds}
        onDone={(next) => {
          onDaysChange?.(next.days)
          onRecurrenceChange?.({
            preset: "custom",
            interval: next.interval,
            unit: "week",
            ends: showRecurrenceEnds ? next.ends : { type: "never" },
          })
          onModeChange?.("recurring")
          setCustomOpen(false)
        }}
      />
    </>
  )
}
